// DraftingIndicator — the quiet chat-side "Working…" status: one live line
// (pen "Chat – every entry", tool activity "Working": "Reading Chapter 4 …").
//
// .claude/research/design/03: while the agent works, the COMPOSER shows a quiet inline
// "Drafting…" (role=status); it RESOLVES when the edits land ("applied"). This
// is that signal — the chat-side status, shown at the composer or as the
// trailing item of the transcript while a turn is in flight.
//
// This is the CHAT-side drafting signal (an in-document sibling sketch,
// Editor/DraftingStatus, was deleted 2026-08-12 — see the DraftingPhase note in
// ../types.ts). Its glyph is the trail line's running spinner, so the live
// line and the settled trail line read as the same row.
//
// Two states, the named `DraftingPhase` sum (not a flag): the agent is drafting
// now, or the run resolved to a tally of applied edits. Both announce via
// role=status (a polite live region) so AT hears the change without a focus
// steal. The resolution message ("N edits applied") is the peak-end signal that
// the turn produced something reviewable.
//
// LIVE ACTIVITY (D51): while drafting, the row says what the agent is doing
// right now ("Reading the source: alvesson.pdf", "Planning the next step"),
// projected from the phase's `activity` (`liveActivityLabel`); "Working" only
// until the turn stream has said. The former "Taking longer than usual" hint is
// gone: once the row names the work, a slow-warning adds anxiety, not
// information.
//
// ELAPSED CLOCK (live-work-surface roadmap step 1): while drafting, the row
// shows the turn's elapsed time ("Reading source: alvesson.pdf… 2:14"), never
// an eternal, silent spinner. It reads the sanctioned clock seam
// (`useElapsedSinceMount`): the transcript mounts this row once per turn and
// keeps it for the whole turn (a material build included), so the clock counts
// from the turn's start and never resets mid-turn (D51). The digits are
// `aria-hidden` so the polite live region is not re-announced every second;
// the activity label carries the meaning for AT.

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { useElapsedSinceMount } from "../../../hooks/useNow.js";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import { formatElapsed } from "../messageTime.js";
import { liveActivityLabel } from "../toolStepLabels.js";
import type { DraftingPhase } from "../types.js";
import "./DraftingIndicator.css";

export type DraftingIndicatorProps = {
    readonly phase: DraftingPhase;
};

const appliedGlyph = <Icon name="check" />;

export const DraftingIndicator = ({ phase }: DraftingIndicatorProps) => {
    const { i18n } = useLingui();
    const drafting = phase.type === "drafting";
    const activity = phase.type === "drafting" ? phase.activity : undefined;
    const elapsedMs = useElapsedSinceMount(1000);

    // While the reply is being written it grows in place with its own dots
    // (pen: "Writing"), so the live line steps aside visually. It stays
    // mounted (the turn's clock keeps counting) and still speaks to AT.
    const writing = activity?.type === "writing_reply";
    return (
        <div
            className={`bilda-drafting-indicator${writing ? " bilda-drafting-indicator--writing" : ""}`}
            role="status"
        >
            {drafting ? (
                <>
                    {/* The live working signal (pen "Chat – every entry", tool
                        activity "Working"): the trail line's running glyph in
                        the agent colour. The whole row is role=status, so work
                        is never on colour or motion alone. */}
                    <Icon
                        name="loading"
                        className="bilda-drafting-indicator__spinner"
                    />
                    {/* What the agent is doing now (D51), on one line;
                        "Working" until the turn stream has said. */}
                    <span className="bilda-drafting-indicator__label">
                        {activity === undefined ? (
                            <Trans>Working</Trans>
                        ) : (
                            liveActivityLabel(activity, (m) => i18n._(m))
                        )}
                        <span
                            className="bilda-drafting-indicator__dots"
                            aria-hidden="true"
                        >
                            <span>.</span>
                            <span>.</span>
                            <span>.</span>
                        </span>
                    </span>
                    {/* aria-hidden: the per-second tick must not re-announce
                        the live region — the label carries the meaning. */}
                    <span
                        className="bilda-drafting-indicator__elapsed"
                        aria-hidden="true"
                    >
                        {formatElapsed(elapsedMs)}
                    </span>
                </>
            ) : (
                <>
                    <span
                        className="bilda-drafting-indicator__glyph bilda-drafting-indicator__glyph--done"
                        aria-hidden="true"
                    >
                        {appliedGlyph}
                    </span>
                    <Text size="sm" tone="dim">
                        <Plural
                            value={phase.count}
                            one="# edit applied"
                            other="# edits applied"
                        />
                    </Text>
                </>
            )}
        </div>
    );
};
